import { useEffect, useMemo, useState } from "react";
import { Box, Button, Container, Grid } from "@mui/material";
import { useUsuario } from "../../../providers/usuarioProvider";
import PrejuizoAcumulado from "./cards/prejuizoAcumulado";
import CardImposto from "./cards/imposto";
import { Moment } from "moment";
import CardResultado from "./cards/resultado";
import Custodia from "./cards/custodia";
import Divida from "./cards/divida";
import Extrato from "./cards/extrato";
import Imposto from "../../../models/imposto";
import Posicao from "../../../models/posicao";
import { filter } from "lodash";
import { TipoOperacao } from "../../../models/enum";
import RenderAnimation from "../../util/ui/renderAnimation";
import { PainelSituacaoCalculo } from "../home/atualizar";
import CardCalendarioMes from "./cards/calendario/calendarioMes";
import FichaDoMes from "./fichaDoMes";
import { IconReportSearch, IconSparkles } from "@tabler/icons-react";
import { useAssinar } from "../assinatura/assine";
import { onSnapshot } from "firebase/firestore";
import PosicaoDb from "../../../db/posicaoDb";
import Isencao from "./cards/isencao";
import AnaliseIADialog from "../fechamento/analiseIADialog";
import { useNavigate } from "react-router-dom";

type Props = {
  mes: Moment;
  imposto?: Imposto;
};

export default function Mes({ mes, imposto }: Props) {
  const navigate = useNavigate();
  const { isLoading, visualizado, claims } = useUsuario();
  const [posicao, setPosicao] = useState<Posicao>();
  const [abrirAnalise, setAbrirAnalise] = useState(false);
  const assinar = useAssinar();

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        new PosicaoDb().doc({
          usuarioId: visualizado,
          posicaoId: mes.format("YYYY-MM-DD"),
        }),
        (snapshot) => setPosicao(snapshot.data())
      );
  }, [visualizado, mes]);

  const fichaDoMes = useMemo(() => <FichaDoMes mes={mes} />, [mes]);
  const cardPrejuizoAcumulado = useMemo(
    () => <PrejuizoAcumulado darf={imposto?.darf} mes={mes} />,
    [mes, imposto?.darf]
  );
  const cardResultado = useMemo(
    () => <CardResultado resultado={imposto?.resultado} mes={mes} />,
    [mes, imposto?.resultado]
  );
  const cardImposto = useMemo(
    () => <CardImposto darf={imposto?.darf} />,
    [mes, imposto?.darf]
  );
  const cardIsencao = useMemo(
    () => <Isencao darf={imposto?.darf} />,
    [mes, imposto?.darf]
  );
  const cardCalendario = useMemo(() => <CardCalendarioMes mes={mes} />, [mes]);
  const cardCustodia = useMemo(
    () => (
      <Custodia
        custodia={filter(
          posicao?.posicoes,
          (p) => p.tipoOperacao === TipoOperacao.COMPRA
        )}
        dataFechamento={posicao?.data}
      />
    ),
    [mes, posicao?.posicoes]
  );
  const cardDivida = useMemo(
    () => (
      <Divida
        divida={filter(
          posicao?.posicoes,
          (p) => p.tipoOperacao === TipoOperacao.VENDA
        )}
        dataFechamento={posicao?.data}
      />
    ),
    [mes, posicao?.posicoes]
  );
  const cardExtrato = useMemo(() => <Extrato mes={mes} />, [mes]);
  return (
    <Container
      maxWidth="lg"
      sx={{
        padding: {
          md: 1,
          xs: 0,
        },
      }}
    >
      <Grid container alignItems="stretch">
        {/* Primeira row: a ficha (que mês, quanto tem) à esquerda e o painel de
            situação do cálculo ocupando o resto. O painel é o bloco de altura
            variável — de uma linha no "tudo em dia" até um erro com parágrafo e
            três ações — então é ele que dita a altura da row. */}
        {/* 316 = os 300px da ficha + o `p: 1` do próprio Grid item, que entra na
            conta porque o item é border-box. No xs a ficha vira full width. */}
        <Grid item xs={12} md="auto" sx={{ p: 1, width: { md: 316 } }}>
          <RenderAnimation
            key="Mês"
            delay={0 * 150}
            render={() => fichaDoMes}
          />
        </Grid>
        <Grid item xs={12} md sx={{ p: 1 }}>
          <RenderAnimation
            key="Situação"
            delay={0 * 150}
            render={() => (
              <PainelSituacaoCalculo
                acoesDaTela={
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                      disabled={isLoading || !imposto?.darf}
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<IconReportSearch size={16} />}
                      onClick={() => {
                        if (!!claims?.stripeRole)
                          navigate(`/timeline/mes/${mes.format("YYYY-MM")}`);
                        else assinar();
                      }}
                    >
                      Ver Detalhes
                    </Button>
                    {/* Secundária: outline na tinta lavanda mantém o sinal de
                        IA sem competir com a ação principal da tela. */}
                    <Button
                      disabled={isLoading || !imposto?.darf}
                      variant="outlined"
                      color="tertiary"
                      size="small"
                      startIcon={<IconSparkles size={16} />}
                      onClick={() => setAbrirAnalise(true)}
                    >
                      Analisar com IA
                    </Button>
                  </Box>
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={12}></Grid>
        <Grid item xs={12} md={6} sx={{ p: 1 }}>
          <RenderAnimation
            key="PrejuizoAcumulado"
            delay={1 * 150}
            render={() => cardPrejuizoAcumulado}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ p: 1 }}>
          <RenderAnimation
            key="Resultado"
            delay={2 * 150}
            render={() => cardResultado}
          />
        </Grid>

        <Grid item xs={12} md={6} sx={{ p: 1 }}>
          <RenderAnimation
            key="Isenção"
            delay={3 * 150}
            render={() => cardIsencao}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ p: 1 }}>
          <RenderAnimation
            key="Imposto"
            delay={4 * 150}
            render={() => cardImposto}
          />
        </Grid>
        <Grid item xs={12} md={12} sx={{ p: 1 }}>
          <RenderAnimation
            key="Calendario"
            delay={5 * 150}
            render={() => cardCalendario}
          />
        </Grid>
        <Grid item xs={12} md={3} sx={{ p: 1 }}>
          <RenderAnimation
            key="Custodia"
            delay={6 * 150}
            render={() => cardCustodia}
          />
        </Grid>
        <Grid item xs={12} md={3} sx={{ p: 1 }}>
          <RenderAnimation
            key="Dívida"
            delay={7 * 150}
            render={() => cardDivida}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ p: 1 }}>
          <RenderAnimation
            key="Extrato"
            delay={8 * 150}
            render={() => cardExtrato}
          />
        </Grid>
      </Grid>
      <AnaliseIADialog
        open={abrirAnalise}
        onClose={() => setAbrirAnalise(false)}
        data={mes}
      />
    </Container>
  );
}
